import { appData } from '@/lib/data';
import {
  addOrRemoveFromArray,
  getQueryString,
  pushParamsToUrl,
} from '@/lib/util';
import {
  useGetCurrentTenantProfileQuery,
  useGetElearningCategoriesQuery,
  useUpdateCategoryAccessMutation,
} from '@/modules/services';
import { ElearningCategoriesType } from '@/modules/types/elearningCategories';
import { Col, DataTable, Row, Search, Spinner, Toggle } from 'elmo-elements';
import React, { useEffect, useState } from 'react';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { Role } from '@/lib/auth/roles/role.enum';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';

const columns = [
  {
    title: 'Category name',
  },
  {
    title: '',
  },
];

const CategoriesComponent = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const hasAdminRole = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [enabledCategories, setEnabledCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const currentQueryString = getQueryString(
    location.search,
    appData.pagination.defaultParams,
  );

  const { data: categoriesData, isLoading: isCategoriesDataLoading } =
    useGetElearningCategoriesQuery(currentQueryString);
  const { data: tenantData } = useGetCurrentTenantProfileQuery();
  const [updateCategoryAccess, { isLoading: isCategoryAccessLoading }] =
    useUpdateCategoryAccessMutation();

  useEffect(() => {
    if (tenantData?.elearningCategoryAccess) {
      const elearningCategories = tenantData.elearningCategoryAccess.split(',');
      setEnabledCategories(elearningCategories);
    }
  }, [tenantData?.elearningCategoryAccess]);

  const onToggle = async (id: string) => {
    const newElearningCategoryAccess = addOrRemoveFromArray(
      enabledCategories,
      id,
    ).join(',');

    await updateCategoryAccess({
      elearningCategoryAccess: newElearningCategoryAccess,
    });
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchKeyword(searchValue);
    if (searchValue === '') pushParamsToUrl({ keyword: '', page: 1 }, history);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    pushParamsToUrl({ page: pageNumber }, history);
  };

  const handlSearch = (keyword: string) => {
    pushParamsToUrl(
      {
        keyword,
        page: 1,
      },
      history,
    );
  };

  if (isCategoriesDataLoading) return <Spinner />;
  if (hasAdminRole !== undefined && !hasAdminRole) {
    history.replace(generatePath(MyLibraryRoutes.path));
  }

  return (
    <div id="Categories">
      <Row className="categories-header">
        <Col span={12}>
          <Search
            value={searchKeyword}
            isVisible
            onChange={(value) => handleSearchChange(value)}
            onSubmit={() => handlSearch(searchKeyword)}
            isSubmitEnabledAlways={true}
          />
        </Col>
      </Row>
      <DataTable
        className="categories-table"
        isLoading={isCategoriesDataLoading}
      >
        <DataTable.Header columns={columns} />
        <DataTable.Body>
          {categoriesData?.categories?.map(
            (category: ElearningCategoriesType) => (
              <DataTable.Tr key={category.id}>
                <DataTable.Td>{category.name}</DataTable.Td>
                <DataTable.Td className="toggle-wrapper">
                  <Toggle
                    disabled={isCategoryAccessLoading}
                    onClick={() => {
                      onToggle(category.id);
                    }}
                    isOn={enabledCategories.includes(category.id)}
                  />
                  {enabledCategories.includes(category.id)
                    ? ' Enabled'
                    : ' Disabled'}
                </DataTable.Td>
              </DataTable.Tr>
            ),
          )}
        </DataTable.Body>
        <DataTable.Pagination
          pageSize={10}
          pageSizeOptions={[10]}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onPageSizeChange={() => {}} // Note: page size isn't supported yet by the backend default size is set to 10.
          totalResults={categoriesData?.count ?? 0}
        />
      </DataTable>
    </div>
  );
};

export default CategoriesComponent;
